import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchArtistRank,
  fetchLabelRanking,
  fetchReleaseRanking,
  fetchRoleRanking,
  fetchTrackRanking,
} from "radar/Service/RadarService";
import { RootStateOrAny } from "react-redux";

export enum ArtistsRankingIndex {
  Performance = "performance",
  DeltaSpotify = "trending",
  PerformanceDevelopment = "overperformance",
  GrowthSpeed = "growth_speed",
  Volatility = "popularity_stability",
}

export enum ReleaseRankingIndex {
  Popularity = "popularity",
  CurrentPopularity = "current_popularity",
  Stability = "stability",
  Top200 = "top200_trending",
  Top1000 = "top1000_trending",
}

export enum TrackRankingIndex {
  Popularity = "popularity",
  CurrentPopularity = "current_popularity",
  Stability = "stability",
  Top200 = "top200_trending",
  Top1000 = "top1000_trending",
}

export enum LabelRankingIndex {
  Concentration = "risk_benefit",
  Popularity = "profit",
  Albuns = "catalog",
  Artists = "current_risk_benefit",
  Profit = "current_profit",
}

export enum RolesRankingIndex {
  Hitmaker = "hitmaker",
  Sniper = "sniper",
  Masses = "masses",
  Trend = "trend",
  TrendMax = "trend-max",
}

interface RankingEnumPayload<T> {
  rankingIndex: T;
  filter: any;
}

interface ReleaseRankingPayload
  extends RankingEnumPayload<ReleaseRankingIndex> {
  timeFrame: any;
}

interface TrackRankingPayload extends RankingEnumPayload<TrackRankingIndex> {
  timeFrame: any;
}

export const getArtistRanking = createAsyncThunk(
  "rankings/get-artist-ranking",
  async (payload: RankingEnumPayload<ArtistsRankingIndex>, { getState }) => {
    const state = getState() as RootStateOrAny;
    const { rankings } = state;
    const { rankingIndex, filter } = payload;
    const { offset, limit } = filter;

    const isCached =
      rankings.artistsRanking[rankingIndex]?.filter?.offset === offset &&
      rankings.artistsRanking[rankingIndex]?.filter?.limit === limit;

    if (isCached) {
      return;
    }

    const artistSortMode = rankingIndex;
    const response = await fetchArtistRank({
      ...filter,
      artistSortMode,
    });

    if (response) {
      return {
        [rankingIndex]: response.artists,
      };
    }
  }
);

export const getReleaseRanking = createAsyncThunk(
  "rankings/get-release-ranking",
  async (payload: ReleaseRankingPayload, { getState }) => {
    const state = getState() as RootStateOrAny;
    const { rankings } = state;
    const { rankingIndex, filter, ...rest } = payload;
    const { offset, limit } = filter;

    const isCached =
      rankings.releasesRanking[rankingIndex]?.filter?.offset === offset &&
      rankings.releasesRanking[rankingIndex]?.filter?.limit === limit;

    if (isCached) {
      return;
    }

    const response = await fetchReleaseRanking({
      ...filter,
      ...rest,
      sortMode: rankingIndex,
    });

    if (response) {
      return {
        [rankingIndex]: response.releaseDate,
      };
    }
  }
);

export const getTrackRanking = createAsyncThunk(
  "rankings/get-track-ranking",
  async (payload: TrackRankingPayload, { getState }) => {
    const state = getState() as RootStateOrAny;
    const { rankings } = state;
    const { rankingIndex, filter } = payload;
    const { offset, limit } = filter;

    const isCached =
      rankings.tracksRanking[rankingIndex]?.filter?.offset === offset &&
      rankings.tracksRanking[rankingIndex]?.filter?.limit === limit;

    if (isCached) {
      return;
    }
    console.log(rankingIndex);
    const response = await fetchTrackRanking({
      ...filter,
      sortMode: rankingIndex,
    });

    if (response) {
      return {
        [rankingIndex]: response.releaseDate,
      };
    }
  }
);

export const getLabelRanking = createAsyncThunk(
  "rankings/get-label-ranking",
  async (payload: RankingEnumPayload<LabelRankingIndex>, { getState }) => {
    const state = getState() as RootStateOrAny;
    const { rankings } = state;
    const { rankingIndex, filter } = payload;
    const { offset, limit } = filter;

    const isCached =
      rankings.labelsRanking[rankingIndex]?.filter?.offset === offset &&
      rankings.labelsRanking[rankingIndex]?.filter?.limit === limit;

    if (isCached) {
      return;
    }

    const response: any = await fetchLabelRanking({
      ...filter,
      sortMode: rankingIndex,
    });

    if (response) {
      return {
        [rankingIndex]: response.labelData,
      };
    }
  }
);

export const getRoleRanking = createAsyncThunk(
  "rankings/get-role-ranking",
  async (payload: RankingEnumPayload<RolesRankingIndex>, { getState }) => {
    const state = getState() as RootStateOrAny;
    const { rankings } = state;
    const { rankingIndex, filter } = payload;
    const { offset, limit } = filter;

    const isCached =
      rankings.rolesRanking[rankingIndex]?.filter?.offset === offset &&
      rankings.rolesRanking[rankingIndex]?.filter?.limit === limit;

    if (isCached) {
      console.log("cached");
      return;
    }

    const response: any = await fetchRoleRanking({
      ...filter,
      sortMode: rankingIndex,
    });

    if (response) {
      return {
        [rankingIndex]: response.roles,
      };
    }
  }
);

export const rankingsSlice = createSlice({
  name: "ranking",
  initialState: {
    artistsRanking: {},
    releasesRanking: {},
    tracksRanking: {},
    labelsRanking: {},
    rolesRanking: {},
    loading: true,
    error: false,
  },
  reducers: {
    clearArtistRanking: (state) => {
      state.artistsRanking = {};
    },
    clearReleasesRanking: (state) => {
      state.releasesRanking = {};
    },
    clearTracksRanking: (state) => {
      state.tracksRanking = {};
    },
    clearLabelsRanking: (state) => {
      state.labelsRanking = {};
    },
    clearRolesRanking: (state) => {
      state.rolesRanking = {};
    },
  },
  extraReducers: {
    [getArtistRanking.pending.type]: (state) => {
      state.loading = true;
    },
    [getArtistRanking.fulfilled.type]: (state, action) => {
      if (action.payload) {
        state.artistsRanking = {
          ...state.artistsRanking,
          ...action.payload,
        };
      }
      state.loading = false;
    },
    [getArtistRanking.rejected.type]: (state, action) => {
      if (!action.payload) {
        state.error = true;
      }
      state.loading = true;
    },
    [getReleaseRanking.pending.type]: (state) => {
      state.loading = true;
    },
    [getReleaseRanking.fulfilled.type]: (state, action) => {
      if (action.payload) {
        console.log(action.payload);
        state.releasesRanking = {
          ...state.releasesRanking,
          ...action.payload,
        };
      }
      state.loading = false;
    },
    [getReleaseRanking.rejected.type]: (state, action) => {
      if (!action.payload) {
        state.error = true;
      }
      state.loading = true;
    },
    [getTrackRanking.pending.type]: (state) => {
      state.loading = true;
    },
    [getTrackRanking.fulfilled.type]: (state, action) => {
      if (action.payload) {
        state.tracksRanking = {
          ...state.tracksRanking,
          ...action.payload,
        };
      }
      state.loading = false;
    },
    [getTrackRanking.rejected.type]: (state, action) => {
      if (!action.payload) {
        state.error = true;
      }
      state.loading = true;
    },
    [getLabelRanking.pending.type]: (state) => {
      state.loading = true;
    },
    [getLabelRanking.fulfilled.type]: (state, action) => {
      if (action.payload) {
        state.labelsRanking = {
          ...state.labelsRanking,
          ...action.payload,
        };
      }
      state.loading = false;
    },
    [getLabelRanking.rejected.type]: (state, action) => {
      if (!action.payload) {
        state.error = true;
      }
      state.loading = true;
    },
    [getRoleRanking.pending.type]: (state) => {
      state.loading = true;
    },
    [getRoleRanking.fulfilled.type]: (state, action) => {
      if (action.payload) {
        state.rolesRanking = {
          ...state.rolesRanking,
          ...action.payload,
        };
      }
      state.loading = false;
    },
    [getRoleRanking.rejected.type]: (state, action) => {
      if (!action.payload) {
        state.error = true;
      }
      state.loading = true;
    },
  },
});

export const {
  clearArtistRanking,
  clearReleasesRanking,
  clearTracksRanking,
  clearLabelsRanking,
  clearRolesRanking,
} = rankingsSlice.actions;

export default rankingsSlice.reducer;
